import { useEffect, useMemo, useState } from "react";
import { createLogger } from "../utils/logger";

const logger = createLogger("usePersistState");

/**
 * 
 * use:  const [counter, setCounter] = usePersistState(0, 'counter');
 * 
 * @param initial_value 
 * @param id 
 * @returns 
 * 
 * export default function usePersistState<T>(initial_value: T, id: string): [T, (new_state: T) => void] {
 */
export default function usePersistState<T>(
  initial_value: T,
  id: string
): [T, (new_state: T) => void] {
  // Set initial value
  const _initial_value = useMemo(() => {
    const local_storage_value_str = localStorage.getItem("state:" + id);
    // If there is a value stored in localStorage, use that
    if (local_storage_value_str && local_storage_value_str.length > 0) {
      const parsed = JSON.parse(local_storage_value_str);
      // Check if parsed value is an empty object
      if (typeof parsed === 'object' && parsed !== null && Object.keys(parsed).length === 0) {
        logger.debug('found empty object for id=', id, 'using initial value');
        return initial_value;
      }
      logger.debug('found record with id=', id);
      return parsed;
    }
    // Otherwise use initial_value that was passed to the function
    return initial_value;
  }, []);

  const [state, setState] = useState(_initial_value);

  //update the state
  useEffect(() => {
    const state_str = JSON.stringify(state); // Stringified state
    localStorage.setItem("state:" + id, state_str); // Set stringified state as item in localStorage
  }, [state]);

  return [state, setState];
}
